import { useTranslation } from "react-i18next";
import { useCallback, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../../utils/cn";
import { Icon } from "./Icon";

export type SegmentedOption = {
  id: string;
  label: string;
  /**
   * Optional leading icon (Iconify name, e.g. `mdi:linux`) rendered before the
   * label. Omit for a text-only segment — the default for every existing
   * consumer.
   */
  icon?: string;
  /** Optional trailing figure (e.g. a mono count) shown after the label. */
  count?: ReactNode;
  /** Render this one segment inert (greyed, skipped by arrow keys). The selected segment stays selectable state. */
  disabled?: boolean;
};

type SegmentedControlProps = {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** Accessible label for the control group */
  ariaLabel?: string;
  /**
   * Unique prefix for generated tab/panel ids. When set, each segment gets an
   * `id` and `aria-controls` that pair with `getTabPanelProps(idBase, value)` on
   * the linked panel (same scheme as `Tabs`). Optional — omit for a plain switch.
   */
  idBase?: string;
  /**
   * When true the whole group is non-interactive: every segment reports
   * `aria-disabled`, drops out of the tab order, and ignores clicks and arrow
   * keys. The selected segment still reads as selected so the value stays clear.
   * Omit for a normal interactive control.
   */
  disabled?: boolean;
  /**
   * Label casing. `"upper"` (default) keeps the established `uppercase
   * tracking-wide` segment styling used by every existing consumer; `"normal"`
   * renders labels as authored (e.g. proper-noun platform names like `macOS`).
   * Opt-in only — omit to leave current callsites unchanged.
   */
  caseStyle?: "upper" | "normal";
  /**
   * Density. `"xs"` is a borderless-surface strip with 24px segments, 11px text and a
   * tinted active segment, for dense toolbars. Omit for the established default.
   */
  size?: "default" | "xs";
};

export const SegmentedControl = ({
  options,
  value,
  onChange,
  className,
  ariaLabel,
  idBase,
  disabled = false,
  caseStyle = "upper",
  size = "default",
}: SegmentedControlProps) => {
  const isXs = size === "xs";
  const { t } = useTranslation("common");
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      if (disabled) return;
      let nextIndex: number | null = null;
      const enabled = (index: number) => !options[index]?.disabled;
      // First enabled segment from `start` stepping by `dir`, wrapping; null when none is enabled.
      const seek = (start: number, dir: 1 | -1): number | null => {
        for (let step = 0; step < options.length; step++) {
          const index = (((start + dir * step) % options.length) + options.length) % options.length;
          if (enabled(index)) return index;
        }
        return null;
      };

      switch (event.key) {
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          nextIndex = seek(currentIndex - 1, -1);
          break;
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          nextIndex = seek(currentIndex + 1, 1);
          break;
        case "Home":
          event.preventDefault();
          nextIndex = seek(0, 1);
          break;
        case "End":
          event.preventDefault();
          nextIndex = seek(options.length - 1, -1);
          break;
      }

      if (nextIndex !== null) {
        const nextOption = options[nextIndex];
        if (nextOption) {
          onChange(nextOption.id);
          // Focus the new tab
          const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
          buttons?.[nextIndex]?.focus();
        }
      }
    },
    [options, onChange, disabled]
  );

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel ?? t($ => $.timeRange)}
      className={cn(
        "inline-flex items-center rounded-md border",
        isXs ? "gap-0.5 border-border/60 p-0.5" : "gap-1 border-border/70 bg-muted/80 p-1 shadow-subtle",
        className
      )}
    >
      {options.map((option, index) => {
        const isActive = option.id === value;
        const segmentDisabled = disabled || (option.disabled === true && !isActive);
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            {...(idBase
              ? { id: `${idBase}-tab-${option.id}`, "aria-controls": `${idBase}-tabpanel-${option.id}` }
              : {})}
            aria-selected={isActive}
            aria-disabled={segmentDisabled || undefined}
            tabIndex={disabled || segmentDisabled ? -1 : isActive ? 0 : -1}
            className={cn(
              isXs ? "h-6 px-2 text-[11px]" : "px-3 py-1.5 text-xs",
              "font-medium rounded-md transition-all",
              (option.icon || isXs) && "inline-flex items-center justify-center gap-1.5",
              isXs && "whitespace-nowrap",
              caseStyle === "upper" && "uppercase tracking-wide",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
              (disabled || segmentDisabled) && "cursor-default",
              segmentDisabled && !disabled && "opacity-50",
              isActive
                ? isXs
                  ? "bg-primary/12 text-primary-text"
                  : "bg-card text-foreground shadow-subtle border border-border"
                : cn("text-muted-foreground", !segmentDisabled && "hover:text-foreground")
            )}
            onClick={segmentDisabled ? undefined : () => onChange(option.id)}
            onKeyDown={segmentDisabled ? undefined : (e) => handleKeyDown(e, index)}
          >
            {option.icon && <Icon icon={option.icon} className="h-4 w-4 shrink-0" aria-hidden />}
            {option.label}
            {option.count !== undefined && <span className="font-mono tabular-nums">{option.count}</span>}
          </button>
        );
      })}
    </div>
  );
};
